import styled from 'styled-components';

export const HeroContainer = styled.div`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 5vw 80px;
  overflow: hidden;
  background: ${({ theme }) => theme.bg || '#07071A'};

  /* Dot grid */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(34, 211, 238, 0.075) 1px, transparent 1px);
    background-size: 38px 38px;
    pointer-events: none;
    mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%);
  }

  @media (max-width: 768px) {
    padding: 100px 24px 60px;
    min-height: 100svh;
  }
`;

export const OrbTopRight = styled.div`
  position: absolute;
  top: -180px;
  right: -180px;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.09) 0%, transparent 65%);
  pointer-events: none;
  animation: float-orb 9s ease-in-out infinite;

  @media (max-width: 768px) {
    width: 400px;
    height: 400px;
    top: -100px;
    right: -100px;
  }
`;

export const OrbBottomLeft = styled.div`
  position: absolute;
  bottom: -200px;
  left: -150px;
  width: 550px;
  height: 550px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.07) 0%, transparent 65%);
  pointer-events: none;
  animation: float-orb 12s ease-in-out infinite reverse;

  @media (max-width: 768px) {
    width: 300px;
    height: 300px;
  }
`;

export const CanvasWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;

  canvas {
    display: block;
    width: 100% !important;
    height: 100% !important;
  }
`;

export const Content = styled.div`
  position: relative;
  z-index: 2;
  max-width: 900px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
`;

export const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  border-radius: 100px;
  border: 1px solid rgba(34, 211, 238, 0.2);
  background: rgba(34, 211, 238, 0.06);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.primary || '#22D3EE'};
  backdrop-filter: blur(8px);
  animation: badge-pulse 3s ease-in-out infinite;

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #22D3EE;
    flex-shrink: 0;
    animation: dot-blink 1.8s ease-in-out infinite;
    box-shadow: 0 0 6px #22D3EE;
  }
`;

export const Eyebrow = styled.p`
  font-family: 'Space Grotesk', monospace;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.24em;
  color: ${({ theme }) => theme.text_secondary || '#94A3B8'};
  text-transform: uppercase;
  margin-top: -4px;
`;

export const Name = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(72px, 12vw, 152px);
  font-weight: 700;
  line-height: 0.9;
  letter-spacing: -0.04em;
  background: linear-gradient(
    120deg,
    #E2E8F0 0%,
    #22D3EE 30%,
    #a5f3fc 45%,
    #E2E8F0 60%,
    #22D3EE 80%,
    #E2E8F0 100%
  );
  background-size: 250% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: name-shimmer 5s linear infinite;

  @media (max-width: 480px) {
    font-size: clamp(56px, 15vw, 84px);
  }
`;

export const RoleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0;

  .sep {
    color: ${({ theme }) => theme.primary || '#22D3EE'};
    margin: 0 10px;
    opacity: 0.5;
  }
`;

export const RoleText = styled.span`
  font-size: clamp(15px, 1.8vw, 20px);
  font-weight: 400;
  color: ${({ theme }) => theme.text_secondary || '#94A3B8'};
  letter-spacing: 0.02em;
`;

export const Description = styled.p`
  font-size: clamp(14px, 1.3vw, 16.5px);
  line-height: 1.78;
  color: ${({ theme }) => theme.text_secondary || '#94A3B8'};
  max-width: 520px;
  opacity: 0.85;
`;

export const CTARow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
`;

export const PrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 34px;
  border-radius: 8px;
  background: #22D3EE;
  color: #07071A;
  font-weight: 700;
  font-size: 14.5px;
  letter-spacing: 0.02em;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(34, 211, 238, 0.25);
  transition: box-shadow 0.3s;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 60%);
  }

  &:hover {
    box-shadow: 0 0 50px rgba(34, 211, 238, 0.45);
  }
`;

export const SecondaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 13px 34px;
  border-radius: 8px;
  border: 1px solid rgba(34, 211, 238, 0.2);
  background: rgba(34, 211, 238, 0.04);
  color: ${({ theme }) => theme.text_primary || '#E2E8F0'};
  font-weight: 500;
  font-size: 14.5px;
  letter-spacing: 0.02em;
  text-decoration: none;
  backdrop-filter: blur(8px);
  transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;

  &:hover {
    border-color: rgba(34, 211, 238, 0.5);
    background: rgba(34, 211, 238, 0.09);
    box-shadow: 0 0 20px rgba(34, 211, 238, 0.12);
  }
`;

export const StatsDivider = styled.div`
  width: 100%;
  max-width: 440px;
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(34, 211, 238, 0.18), transparent);
`;

export const StatsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 48px;

  @media (max-width: 480px) {
    gap: 28px;
  }
`;

export const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const StatValue = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  color: ${({ theme }) => theme.primary || '#22D3EE'};
  letter-spacing: -0.03em;
  line-height: 1;
`;

export const StatLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.text_secondary || '#94A3B8'};
  opacity: 0.7;
`;

export const SocialRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

export const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.07);
  color: ${({ theme }) => theme.text_secondary || '#94A3B8'};
  text-decoration: none;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(6px);
  transition: border-color 0.2s, color 0.2s, background 0.2s, transform 0.2s;

  &:hover {
    border-color: rgba(34, 211, 238, 0.4);
    color: #22D3EE;
    background: rgba(34, 211, 238, 0.07);
    transform: translateY(-3px);
  }
`;

export const ScrollHint = styled.div`
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 2;

  span {
    font-size: 9px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.text_secondary || '#94A3B8'};
    opacity: 0.45;
  }

  .line {
    width: 1px;
    height: 44px;
    background: linear-gradient(to bottom, rgba(34, 211, 238, 0.5), transparent);
    animation: pulse-line 2.2s ease-in-out infinite;
  }

  @keyframes pulse-line {
    0%, 100% { opacity: 0.3; }
    50%       { opacity: 1; }
  }

  @media (max-width: 768px) { display: none; }
`;
